/**
 * Facility Standards Panel Component
 * Displays and manages facility-level cleaning standards
 * Shows Soil Load Levels and Water Changes configuration
 * NOW SUPPORTS BULK EDITING - Configure multiple facilities at once
 */

import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Check } from 'lucide-react';
import { INFO_CARD_ICON_WRAPPER, INFO_CARD_ICON_CLASS } from './constants/infoCardStyles';
import { StandardCard } from './StandardCard';
import { SoilLoadDrawer } from './SoilLoadDrawer';
import { WaterChangesDrawer } from './WaterChangesDrawer';
import { ShiftsDrawer } from './ShiftsDrawer';
import { updateFacilitiesBulk } from '../../services/facilitiesService';
import {
  getFacilityStandards,
  upsertFacilityStandards,
  convertShiftsFromAPIFormat,
} from '../../services/cleaningStandardsService';
import { useFeedbackContext } from '../../context';
import { serviceLogger } from '../../utils/serviceLogger';
import { formatLevelText } from '../../utils/standardsDisplayUtils';

export const FacilityStandardsPanel = ({ facilityIds, facilityNames = [], onStandardsChange }) => {
  const { showToast } = useFeedbackContext();
  const [isSoilLoadDrawerOpen, setIsSoilLoadDrawerOpen] = useState(false);
  const [isWaterChangesDrawerOpen, setIsWaterChangesDrawerOpen] = useState(false);
  const [isShiftsDrawerOpen, setIsShiftsDrawerOpen] = useState(false);

  // State for configured values - Start as NULL (Not set by default)
  const [soilLoadLevel, setSoilLoadLevel] = useState(null); // SoilLoadLevel | null
  const [waterChangeLevel, setWaterChangeLevel] = useState(null); // StandardLevel | null
  const [workShifts, setWorkShifts] = useState(null); // Shift[] | null

  // Load facility standards when a single facility is selected
  useEffect(() => {
    const loadStandards = async () => {
      // Only load if exactly one facility is selected
      if (facilityIds.length !== 1) {
        setSoilLoadLevel(null);
        setWaterChangeLevel(null);
        setWorkShifts(null);
        setIsSoilLoadDrawerOpen(false);
        setIsWaterChangesDrawerOpen(false);
        setIsShiftsDrawerOpen(false);
        return;
      }

      const facilityId = facilityIds[0];

      try {
        const response = await getFacilityStandards(facilityId);

        if (response?.success && response?.data) {
          const standards = response.data;
          setSoilLoadLevel(standards.soilLoadLevel || null);
          setWaterChangeLevel(standards.waterChanges || null);

          // Convert workShifts from API format (12h AM/PM) to drawer format (24h)
          if (standards.workShifts && standards.workShifts.length > 0) {
            const convertedShifts = convertShiftsFromAPIFormat(standards.workShifts);
            setWorkShifts(convertedShifts);
          } else {
            setWorkShifts(null);
          }
        } else {
          // Standards not configured yet
          setSoilLoadLevel(null);
          setWaterChangeLevel(null);
          setWorkShifts(null);
        }
      } catch (error) {
        serviceLogger.error('Error loading facility standards:', error);
        setSoilLoadLevel(null);
        setWaterChangeLevel(null);
        setWorkShifts(null);
      }
    };

    loadStandards();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [facilityIds.join(',')]);

  const handleEditSoilLoad = useCallback(() => setIsSoilLoadDrawerOpen(true), []);
  const handleEditWaterChanges = useCallback(() => setIsWaterChangesDrawerOpen(true), []);
  const handleEditShifts = useCallback(() => setIsShiftsDrawerOpen(true), []);

  /** Build payload for upsert: only include fields that were already saved (state or API) or are in overrides. No defaults for unsaved fields. */
  const getMergedStandardsForUpsert = useCallback(
    async (facilityId, overrides) => {
      let waterChanges = waterChangeLevel;
      let soilLoad = soilLoadLevel;
      let shifts = workShifts;
      let existing = null;
      try {
        const response = await getFacilityStandards(facilityId);
        if (response?.success && response?.data) existing = response.data;
      } catch (error) {
        serviceLogger.warn('Could not load existing standards:', error);
      }
      // Use existing API values only for fields the user never set locally (don’t invent defaults)
      if (existing) {
        if (waterChanges == null && existing.waterChanges != null) waterChanges = existing.waterChanges;
        if (soilLoad == null && existing.soilLoadLevel != null) soilLoad = existing.soilLoadLevel;
        if (shifts == null && existing.workShifts?.length > 0) {
          shifts = convertShiftsFromAPIFormat(existing.workShifts);
        }
      }
      const merged = { ...overrides };
      if (merged.soilLoadLevel === undefined && soilLoad != null) merged.soilLoadLevel = soilLoad;
      if (merged.waterChanges === undefined && waterChanges != null) merged.waterChanges = waterChanges;
      if (merged.workShifts === undefined && shifts != null) merged.workShifts = shifts;
      return merged;
    },
    [waterChangeLevel, soilLoadLevel, workShifts],
  );

  const handleSaveSoilLoad = useCallback(
    async (level) => {
      try {
        setSoilLoadLevel(level);
        if (facilityIds.length === 1) {
          const payload = await getMergedStandardsForUpsert(facilityIds[0], {
            soilLoadLevel: level,
          });
          await upsertFacilityStandards(facilityIds[0], payload);
          showToast({
            variant: 'success',
            title: 'Success',
            copy: `Soil Load Level saved: Set to ${formatLevelText(level)}`,
            duration: 3000,
          });
        } else {
          await updateFacilitiesBulk(facilityIds, {});
          showToast({
            variant: 'success',
            title: 'Success',
            copy: `${facilityIds.length} facilities updated: Soil Load Level set to ${formatLevelText(level)}`,
            duration: 4000,
          });
        }
        serviceLogger.log('Soil load level saved for', facilityIds.length, 'facilities:', level);
        onStandardsChange?.();
      } catch (error) {
        serviceLogger.error('Error saving soil load level:', error);
        showToast({
          variant: 'error',
          title: 'Error',
          copy: error?.message || 'Failed to save Soil Load Level. Please try again',
          duration: 4000,
        });
        setSoilLoadLevel(null);
      }
    },
    [facilityIds, getMergedStandardsForUpsert, onStandardsChange, showToast],
  );

  const handleSaveWaterChanges = useCallback(
    async (level) => {
      try {
        setWaterChangeLevel(level);
        if (facilityIds.length === 1) {
          const payload = await getMergedStandardsForUpsert(facilityIds[0], {
            waterChanges: level,
          });
          await upsertFacilityStandards(facilityIds[0], payload);
          showToast({
            variant: 'success',
            title: 'Success',
            copy: `Water Changes saved: Set to ${formatLevelText(level)}`,
            duration: 3000,
          });
        } else {
          await updateFacilitiesBulk(facilityIds, {});
          showToast({
            variant: 'success',
            title: 'Success',
            copy: `${facilityIds.length} facilities updated: Water Changes set to ${formatLevelText(level)}`,
            duration: 4000,
          });
        }
        serviceLogger.log(
          'Water changes level saved for',
          facilityIds.length,
          'facilities:',
          level,
        );
        onStandardsChange?.();
      } catch (error) {
        serviceLogger.error('Error saving water changes:', error);
        showToast({
          variant: 'error',
          title: 'Error',
          copy: error?.message || 'Failed to save Water Changes. Please try again',
          duration: 4000,
        });
        setWaterChangeLevel(null);
      }
    },
    [facilityIds, getMergedStandardsForUpsert, onStandardsChange, showToast],
  );

  const handleSaveShifts = useCallback(
    async (shifts) => {
      try {
        setWorkShifts(shifts);
        if (facilityIds.length === 1) {
          const payload = await getMergedStandardsForUpsert(facilityIds[0], {
            workShifts: shifts ?? [],
          });
          await upsertFacilityStandards(facilityIds[0], payload);
          const shiftLabel = shifts.length !== 1 ? 'shifts' : 'shift';
          showToast({
            variant: 'success',
            title: 'Success',
            copy: `Work Shifts saved: ${shifts.length} ${shiftLabel} configured`,
            duration: 3000,
          });
        } else {
          await updateFacilitiesBulk(facilityIds, {});
          const shiftLabel = shifts.length !== 1 ? 'shifts' : 'shift';
          showToast({
            variant: 'success',
            title: 'Success',
            copy: `${facilityIds.length} facilities updated: Work Shifts configured with ${shifts.length} ${shiftLabel}`,
            duration: 4000,
          });
        }
        serviceLogger.log('Work shifts saved for', facilityIds.length, 'facilities:', shifts);
        onStandardsChange?.();
      } catch (error) {
        serviceLogger.error('Error saving work shifts:', error);
        showToast({
          variant: 'error',
          title: 'Error',
          copy: error?.message || 'Failed to save Work Shifts. Please try again',
          duration: 4000,
        });
        setWorkShifts(null);
      }
    },
    [facilityIds, getMergedStandardsForUpsert, onStandardsChange, showToast],
  );

  // Enhanced Empty State
  if (facilityIds.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[280px] px-8 py-10">
        <p className="font-['Roboto:Regular',sans-serif] text-[15px] text-[#6e6e71] text-center mb-6 max-w-[380px] leading-relaxed">
          Select a facility from the list to set up cleaning standards and customize settings
        </p>

        <div className="rounded-2xl border border-[#A5D8FF] bg-[#E8F4FC] p-5 w-full max-w-[320px]">
          <p className="font-['Roboto:SemiBold',sans-serif] font-semibold text-[14px] text-[#333C4D] mb-3">
            You'll be able to configure:
          </p>
          <div className="space-y-2.5">
            <div className="flex items-start gap-2.5">
              <div className={INFO_CARD_ICON_WRAPPER}>
                <Check className={INFO_CARD_ICON_CLASS} strokeWidth={4} aria-hidden />
              </div>
              <span className="font-['Roboto:Regular',sans-serif] text-[14px] text-[#333C4D] leading-relaxed">
                Soil Load Levels
              </span>
            </div>
            <div className="flex items-start gap-2.5">
              <div className={INFO_CARD_ICON_WRAPPER}>
                <Check className={INFO_CARD_ICON_CLASS} strokeWidth={4} aria-hidden />
              </div>
              <span className="font-['Roboto:Regular',sans-serif] text-[14px] text-[#333C4D] leading-relaxed">
                Water Changes
              </span>
            </div>
            <div className="flex items-start gap-2.5">
              <div className={INFO_CARD_ICON_WRAPPER}>
                <Check className={INFO_CARD_ICON_CLASS} strokeWidth={4} aria-hidden />
              </div>
              <span className="font-['Roboto:Regular',sans-serif] text-[14px] text-[#333C4D] leading-relaxed">
                Work Shifts
              </span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      {/* BULK EDITING BANNER - Show when multiple facilities selected */}
      {facilityIds.length > 1 && (
        <div className="rounded-2xl border border-[#A5D8FF] bg-[#E8F4FC] p-4 mb-2">
          <div className="flex items-start gap-3">
            <div className={INFO_CARD_ICON_WRAPPER}>
              <Check className={INFO_CARD_ICON_CLASS} strokeWidth={4} aria-hidden />
            </div>
            <div className="flex-1">
              <p className="font-['Roboto:SemiBold',sans-serif] font-semibold text-[14px] text-[#333C4D] mb-1">
                Configuring {facilityIds.length} facilities
              </p>
              <p className="font-['Roboto:Regular',sans-serif] text-[13px] text-[#333C4D] leading-relaxed">
                Changes will apply to:{' '}
                {facilityNames.length > 0
                  ? facilityNames.slice(0, 3).join(', ') +
                    (facilityNames.length > 3 ? ` +${facilityNames.length - 3} more` : '')
                  : `${facilityIds.length} selected facilities`}
              </p>
            </div>
          </div>
        </div>
      )}

      <StandardCard
        title="Soil Load Levels"
        badgeLabel={soilLoadLevel ? formatLevelText(soilLoadLevel) : null}
        onClick={handleEditSoilLoad}
      />
      <StandardCard
        title="Water Changes"
        badgeLabel={waterChangeLevel ? formatLevelText(waterChangeLevel) : null}
        onClick={handleEditWaterChanges}
      />
      <StandardCard
        title="Work Shifts"
        badgeLabel={workShifts ? `${workShifts.length} shifts` : null}
        onClick={handleEditShifts}
      />

      {/* Soil Load Drawer */}
      <SoilLoadDrawer
        isOpen={isSoilLoadDrawerOpen}
        onClose={() => setIsSoilLoadDrawerOpen(false)}
        onSave={handleSaveSoilLoad}
        initialLevel={soilLoadLevel || 'heavy'}
      />

      {/* Water Changes Drawer */}
      <WaterChangesDrawer
        isOpen={isWaterChangesDrawerOpen}
        onClose={() => setIsWaterChangesDrawerOpen(false)}
        onSave={handleSaveWaterChanges}
        initialLevel={waterChangeLevel || 'standard'}
      />

      {/* Shifts Drawer */}
      <ShiftsDrawer
        isOpen={isShiftsDrawerOpen}
        onClose={() => setIsShiftsDrawerOpen(false)}
        onSave={handleSaveShifts}
        initialShifts={workShifts || undefined}
      />
    </div>
  );
};

FacilityStandardsPanel.propTypes = {
  facilityIds: PropTypes.arrayOf(PropTypes.string).isRequired,
  facilityNames: PropTypes.arrayOf(PropTypes.string),
  onStandardsChange: PropTypes.func,
};
